import PropTypes from 'prop-types';

import { TOOL_TYPE } from 'core/chatbot/utils/parsers/ToolType';
import Tooltip from 'core/modules/atoms/Tooltip/Tooltip';
import Markdown from 'core/modules/components/Markdown/Markdown';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useAnalytics from '../../../../hooks/useAnalytics';
import { ReactComponent as WarningIcon } from '../../../assets/icons/warning.svg';
import { EVENT_NAMES } from '../../../constants/eventNames';
import messageContentParser from '../../../utils/MessageContentParser';
import ChatContentReferences from '../ChatContentReferences/ChatContentReferences';
import ChatD3Modal from '../ChatD3Modal/ChatD3Modal';
import ChatD3VisualizationCard from '../ChatD3VisualizationCard/ChatD3VisualizationCard';
import ChatIconButton from '../ChatIconButton';
import ChatImageModal from '../ChatImageModal/ChatImageModal';
import ChatMermaidCard from '../ChatMermaidCard/ChatMermaidCard';
import ChatMermaidModal from '../ChatMermaidModal/ChatMermaidModal';
import ToolIndicator from '../ToolIndicator/ToolIndicator';
import TypingIndicator from '../TypingIndicator/TypingIndicator';
import './ChatMessage.scss';
import { useMessageActions } from './useMessageActions';

function ChatMessage({ message, className, activeTools }) {
  const { t } = useTranslation(['chatbot']);
  const { trackEvent } = useAnalytics();
  const isBot = message.sender === 'bot';
  const { isStreaming, isError, justPosted } = message;
  const [modalData, setModalData] = useState({ isOpen: false, url: '', title: '' });
  const [mermaidModalData, setMermaidModalData] = useState({ isOpen: false, mermaidCode: '', title: '' });
  const [imageModalData, setImageModalData] = useState({ isOpen: false, imageSrc: '', imageName: '', title: '' });

  // Parse message content using the modular parser system
  const parseMessageContent = useCallback((content) => {
    return messageContentParser.parseContent(content);
  }, []);

  // Handle opening the D3 visualization modal
  const handleOpenVisualization = useCallback(
    (url, title, type) => {
      setModalData({ isOpen: true, url, title });

      // Track analytics event for visualization opened
      try {
        trackEvent(EVENT_NAMES.ARTIFACT_OPENED, {
          type,
        });
      } catch (analyticsError) {
        console.error('Failed to track visualization opened event:', analyticsError);
      }
    },
    [trackEvent]
  );

  // Handle closing the D3 modal
  const handleCloseModal = useCallback(() => {
    setModalData({ isOpen: false, url: '', title: '' });
  }, []);

  // Handle opening the Mermaid diagram modal
  const handleOpenMermaidDiagram = useCallback(
    (mermaidCode, title) => {
      setMermaidModalData({ isOpen: true, mermaidCode, title });

      // Track analytics event for mermaid diagram opened
      try {
        trackEvent(EVENT_NAMES.ARTIFACT_OPENED, {
          type: TOOL_TYPE.MERMAID_DIAGRAM,
        });
      } catch (analyticsError) {
        console.error('Failed to track mermaid diagram opened event:', analyticsError);
      }
    },
    [trackEvent]
  );

  // Handle closing the Mermaid modal
  const handleCloseMermaidModal = useCallback(() => {
    setMermaidModalData({ isOpen: false, mermaidCode: '', title: '' });
  }, []);

  // Handle opening the image modal
  const handleOpenImageModal = useCallback(
    (imageSrc, imageName) => {
      setImageModalData({ isOpen: true, imageSrc, imageName, title: imageName || t('chatbot:Image') });

      // Track analytics event for image opened
      try {
        trackEvent(EVENT_NAMES.ARTIFACT_OPENED, {
          type: TOOL_TYPE.IMAGE,
        });
      } catch (analyticsError) {
        console.error('Failed to track image opened event:', analyticsError);
      }
    },
    [trackEvent, t]
  );

  // Handle closing the image modal
  const handleCloseImageModal = useCallback(() => {
    setImageModalData({ isOpen: false, imageSrc: '', imageName: '', title: '' });
  }, []);

  // Use the message actions hook
  const { handleCopyMessage, showCopySuccess, handlePlayAudio, isPlayingAudio, isLoadingAudio } = useMessageActions({
    message,
  });

  // Parse the message content
  const parsedContent = useMemo(() => parseMessageContent(message.text), [message.text, parseMessageContent]);

  // Extraer referencias de contenido para mostrar en el footer
  const contentReferences = useMemo(() => {
    const references = [];
    parsedContent.forEach((part) => {
      if (part.type === TOOL_TYPE.CONTENT_REFERENCES && part.references) {
        references.push(...part.references);
      }
    });
    return references;
  }, [parsedContent]);

  // Si es un mensaje en streaming de un bot, solo mostrar el indicador de escritura
  if (isBot && isStreaming && !message.text && !activeTools?.length) {
    return (
      <div className={`chat-message ${className || ''} chat-message--streaming`}>
        <div className="chat-bubble chat-bubble--bot">
          <div className="chat-bubble__avatar" />
          <div className="chat-bubble__content">
            <TypingIndicator />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`chat-message ${isError ? 'chat-message--error' : ''} ${className || ''}`}>
      <div
        className={`chat-bubble ${isBot ? 'chat-bubble--bot' : 'chat-bubble--user'} ${justPosted ? 'chat-bubble--just-posted' : ''} ${
          isError ? 'chat-message__bubble--error' : ''
        }`}
      >
        {isBot && <div className="chat-bubble__avatar" />}

        <div className="chat-bubble__content">
          {isError && (
            <div className="chat-message__error-icon">
              <WarningIcon className="chat-message__error-icon-image" />
            </div>
          )}
          <div className="chat-bubble__message">
            <div className="chat-bubble__message-content">
              {isError && <div>{t('Error')}</div>}

              {!isError && (
                <>
                  {/* Display attachments (images) before text content */}
                  {message.attachments && message.attachments.length > 0 && (
                    <div className="chat-bubble__attachments">
                      {message.attachments.map((attachment, index) => (
                        <div key={index} className="chat-bubble__image">
                          <img
                            src={attachment.url}
                            alt={attachment.fileName || t('Attached image')}
                            onClick={() => handleOpenImageModal(attachment.url, attachment.fileName)}
                            style={{ cursor: 'pointer' }}
                            onError={(e) => {
                              e.target.style.display = 'none'; // Hide broken images
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Keep support for legacy single image uploads */}
                  {!message.attachments && message.image && (
                    <div className="chat-bubble__image">
                      <img
                        src={message.image}
                        alt={message.imageName || t('Uploaded image')}
                        onClick={() => handleOpenImageModal(message.image, message.imageName)}
                        style={{ cursor: 'pointer' }}
                      />
                    </div>
                  )}

                  {/* Display text content */}
                  {parsedContent.map((part, index) => {
                    if (part.type === 'text') {
                      return <Markdown key={index}>{part.content}</Markdown>;
                    } else if (
                      part.type === TOOL_TYPE.D3_VISUALIZATION ||
                      part.type === TOOL_TYPE.GEOGEBRA_VISUALIZATION ||
                      part.type === TOOL_TYPE.THREEJS_VISUALIZATION
                    ) {
                      return (
                        <ChatD3VisualizationCard
                          key={index}
                          url={part.url}
                          title={part.title}
                          onOpen={(url, title) => handleOpenVisualization(url, title, part.type)}
                        />
                      );
                    } else if (part.type === TOOL_TYPE.MERMAID_DIAGRAM) {
                      return <ChatMermaidCard key={index} mermaidCode={part.mermaidCode} title={part.title} onOpen={handleOpenMermaidDiagram} />;
                    } else if (part.type === TOOL_TYPE.CONTENT_REFERENCES) {
                      // Las referencias se renderizan en el footer, no inline
                      return null;
                    }
                    return null;
                  })}
                </>
              )}
              {/* Show tool indicators for active tools */}
              {isBot && activeTools && activeTools.length > 0 && (
                <div className="chat-bubble__tool-indicators">
                  {activeTools.map((event) => (
                    <ToolIndicator key={event.id} toolEvent={event} />
                  ))}
                </div>
              )}
            </div>
            <div className="chat-bubble__message-references">
              {/* Mostrar referencias de contenido en el footer si existen */}
              {contentReferences.length > 0 && <ChatContentReferences references={contentReferences} />}
            </div>
          </div>
        </div>
      </div>

      {/* Copy and audio buttons for bot messages - below bubble on the left */}
      {isBot && !isError && !isStreaming && message.text && (
        <div className="chat-message__actions">
          <Tooltip title={showCopySuccess ? t('Copied!') : t('Copy message')} placement="top" variant="dark" size="small" arrow>
            <ChatIconButton
              icon={showCopySuccess ? 'check' : 'copy'}
              onClick={handleCopyMessage}
              disabled={showCopySuccess}
              size="small"
              variant="default"
              className="chat-message__copy-button"
              ariaLabel={showCopySuccess ? t('Copied!') : t('Copy message')}
            />
          </Tooltip>
          <Tooltip title={isPlayingAudio ? t('Pause audio') : t('Play message')} placement="top" variant="dark" size="small" arrow>
            <ChatIconButton
              icon={isPlayingAudio ? 'stop' : 'volume-up'}
              onClick={handlePlayAudio}
              disabled={isLoadingAudio}
              size="small"
              variant="default"
              className={`chat-message__audio-button ${isLoadingAudio ? 'chat-message__audio-button--loading' : ''}`}
              ariaLabel={isPlayingAudio ? t('Pause audio') : t('Play message')}
            />
          </Tooltip>
        </div>
      )}

      <ChatD3Modal isOpen={modalData.isOpen} onClose={handleCloseModal} url={modalData.url} title={modalData.title} />
      <ChatMermaidModal
        isOpen={mermaidModalData.isOpen}
        onClose={handleCloseMermaidModal}
        mermaidCode={mermaidModalData.mermaidCode}
        title={mermaidModalData.title}
      />
      <ChatImageModal
        isOpen={imageModalData.isOpen}
        onClose={handleCloseImageModal}
        imageSrc={imageModalData.imageSrc}
        imageName={imageModalData.imageName}
        title={imageModalData.title}
      />
    </div>
  );
}

ChatMessage.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    text: PropTypes.string.isRequired,
    sender: PropTypes.oneOf(['user', 'bot']).isRequired,
    timestamp: PropTypes.instanceOf(Date).isRequired,
    isStreaming: PropTypes.bool,
    isError: PropTypes.bool,
    justPosted: PropTypes.bool,
    image: PropTypes.string,
    imageName: PropTypes.string,
    attachments: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string.isRequired,
        contentType: PropTypes.string.isRequired,
        fileName: PropTypes.string.isRequired,
        sizeInBytes: PropTypes.number.isRequired,
        createdAt: PropTypes.string.isRequired,
      })
    ),
  }).isRequired,
  className: PropTypes.string,
  activeTools: PropTypes.array,
};

ChatMessage.defaultProps = {
  className: '',
  activeTools: [],
};

export default ChatMessage;
